import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DayPickerValues } from './dayPickerValues';
import { DateRange } from '@energy-stacks/core/date-range-picker';
import { useCallback, useState } from 'react';
import { getRangeForTimePeriod } from './getRangeForTimePeriod';
import { TableDateTimeFilter } from '../TableDateTimeFilter';
import { DashboardTimePickerToggleButtonGroup } from './DashboardTimePickerToggleButtonGroup';

interface DashboardTimePickerProps {
  onDateRangeChange: (dateRange: DateRange | undefined) => void;
}

type Mode = 'preset' | 'custom';

export const DashboardTimePicker = ({
  onDateRangeChange,
}: DashboardTimePickerProps) => {
  const { t } = useTranslation('dashboard');

  const [mode, setMode] = useState<Mode>('preset');
  const [activePreset, setActivePreset] = useState<DayPickerValues | null>('1');

  const [customRange, setCustomRange] = useState<DateRange | undefined>(
    undefined
  );

  const handlePresetChange = (value: DayPickerValues) => {
    setMode('preset');
    setActivePreset(value);

    const { startDate, endDate } = getRangeForTimePeriod(value);

    onDateRangeChange({
      startDate,
      endDate,
    });
  };

  const handleClearDateRange = useCallback(() => {
    setCustomRange(undefined);
    setMode('preset');

    if (activePreset) {
      const { startDate, endDate } = getRangeForTimePeriod(activePreset);
      onDateRangeChange({
        startDate,
        endDate,
      });
    } else {
      onDateRangeChange(undefined);
    }
  }, [activePreset, onDateRangeChange]);

  const handleApplyDateRange = useCallback(
    (range: DateRange) => {
      setMode('custom');
      setActivePreset(null);
      setCustomRange(range);

      onDateRangeChange(range);
    },
    [onDateRangeChange]
  );

  return (
    <Box display="flex" alignItems="center" gap="20px">
      <DashboardTimePickerToggleButtonGroup
        value={mode === 'preset' ? activePreset : null}
        onChange={handlePresetChange}
      />
      <TableDateTimeFilter
        title={t('timeRange')}
        isActive={mode === 'custom'}
        defaultDateRange={customRange}
        onDateRangeApplied={handleApplyDateRange}
        onDateRangeCleared={handleClearDateRange}
      />
    </Box>
  );
};
